import React from "react";
import "twin.macro";

import { Event, CheckoutDetails } from "api/v2/event.types";

import { Link } from "react-router-dom";

import { useCart } from "components/CartProvider";
import { Button, P } from "components/typography";
import { useAuth } from "components/AuthProvider";
import { DWELLSOCIAL_RESERVATION_FEE } from "config/constants";

import EventHeaderCard from "./eventHeaderCard";

import OrderSummaryList, { CartLi } from "components/composites/OrderSummary";

const parsePrice = (price: string | number) =>
  typeof price === "string" ? parseFloat(price) : price;

// Helper function to get tax amount from checkout details
const getTaxAmount = (checkoutDetails?: CheckoutDetails): number | null => {
  if (!checkoutDetails?.baseCost || !checkoutDetails?.total) {
    return null;
  }

  const taxLineItem = checkoutDetails.nonProductLineItems?.find(
    (li) => li.sku === "tax"
  );

  return taxLineItem ? parsePrice(taxLineItem.price) : null;
};

const OrderSummary: React.FC<{
  eventId: string;
  event: Event;
  checkoutDetails?: CheckoutDetails;
  isLoading: boolean;
}> = ({ eventId, event, checkoutDetails, isLoading }) => {
  const cart = useCart();
  const { user } = useAuth();

  const eventCart = cart.state[eventId];
  const productLineItems = eventCart.items.map((item) => ({
    name: item.product.name,
    price: item.product.price,
    notes: item.notes,
    quantity: item.quantity,
    optionChoices: item.optionChoices
      ? Object.values(item.optionChoices)
      : null,
    sku: null
  }));

  const nonProductLineItems = checkoutDetails?.nonProductLineItems
    ?.filter((li) => !["credit", "promo_code", "d2d"].includes(li.sku))
    .map((li) => ({
      ...li,
      quantity: 1,
      price: parsePrice(li.price)
    }));
  const creditLineItem = checkoutDetails?.nonProductLineItems?.find(
    (li) => li.name === "Credit"
  );
  const promoCodeLineItem = checkoutDetails?.nonProductLineItems?.find(
    (li) => li.name === "Promo Code"
  );
  const d2dLineItem = checkoutDetails?.nonProductLineItems?.find(
    (li) => li.sku === "d2d"
  );

  return (
    <aside tw="w-full lg:w-1/5 bg-secondary py-10 px-6 mb-24 md:mb-0">
      <EventHeaderCard event={event} />
      <OrderSummaryList
        isLoading={isLoading}
        productLineItems={productLineItems}
        nonProductLineItems={nonProductLineItems}
        promoCodeLineItem={
          promoCodeLineItem && {
            ...promoCodeLineItem,
            quantity: 1,
            price: parsePrice(promoCodeLineItem?.price)
          }
        }
        creditLineItem={
          creditLineItem && {
            ...creditLineItem,
            quantity: 1,
            price: parsePrice(creditLineItem?.price)
          }
        }
        d2dLineItem={
          d2dLineItem && {
            ...d2dLineItem,
            quantity: 1,
            price: parsePrice(d2dLineItem?.price)
          }
        }
        subtotal={checkoutDetails?.baseCost}
        totalWithGratuity={checkoutDetails?.preadjustmentTotal}
        totalDue={checkoutDetails?.total}
      >
        <CartLi>
          <Link
            to={`/events/${eventId}`}
            component={(props) => (
              <Button.Info as="a" {...props} tw="w-full text-center py-3" />
            )}
          >
            <i className="fas fa-pencil-alt" /> Edit your order
          </Link>
        </CartLi>
      </OrderSummaryList>

      {/*VIP Membership Savings Text*/}
      {user?.stripe_subscribed_at && (
        <div tw="mt-4">
          <ul tw="rounded bg-white p-0 m-0 list-none">
            <li tw="px-6 py-4 border-0 border-b last:border-b-0 border-solid border-secondary">
              <p tw="text-sm text-info-dark font-medium text-center">
                {(() => {
                  const taxAmount = getTaxAmount(checkoutDetails);

                  if (
                    taxAmount !== null &&
                    checkoutDetails?.baseCost &&
                    checkoutDetails?.total
                  ) {
                    const subtotal = parsePrice(checkoutDetails.baseCost);
                    const currentTotal = parsePrice(checkoutDetails.total);

                    // Set fee amount based on restaurant - do-rite donuts gets 18% instead of 20%
                    const feeAmount = event.restaurantName
                      .toLowerCase()
                      .includes("do-rite")
                      ? 0.18
                      : 0.2;
                    const dwellsocialFee = DWELLSOCIAL_RESERVATION_FEE;

                    // Calculate non VIP total: subtotal + tax + (fee% of subtotal) + dwellsocialFee
                    const noVIPTotal =
                      subtotal +
                      taxAmount +
                      subtotal * feeAmount +
                      dwellsocialFee;

                    // Calculate realized savings
                    const realizedSavings = noVIPTotal - currentTotal;

                    if (realizedSavings > 0) {
                      return (
                        <P.Small tw="font-bold mt-6">
                          You saved ${realizedSavings.toFixed(2)} on this order
                          by being a VIP Member!
                        </P.Small>
                      );
                    }
                  }

                  // Return null if no savings to show
                  return (
                    <P.Small tw="font-bold mt-6">
                      You saved on this order by being a VIP Member!
                    </P.Small>
                  );
                })()}
              </p>
            </li>
          </ul>
        </div>
      )}

      {/* VIP Membership Promotional Text */}
      {!user?.stripe_subscribed_at && (
        <div tw="mt-4">
          <ul tw="rounded bg-white p-0 m-0 list-none">
            <li tw="px-6 py-4 border-0 border-b last:border-b-0 border-solid border-secondary">
              <p tw="text-sm text-info-dark font-medium text-center">
                {(() => {
                  const taxAmount = getTaxAmount(checkoutDetails);

                  if (
                    taxAmount !== null &&
                    checkoutDetails?.baseCost &&
                    checkoutDetails?.total
                  ) {
                    const subtotal = parsePrice(checkoutDetails.baseCost);
                    const currentTotal = parsePrice(checkoutDetails.total);

                    // Calculate VIP total: subtotal + tax + (15% of subtotal)
                    const vipTotal = subtotal + taxAmount + subtotal * 0.15;

                    // Calculate potential savings
                    const potentialSavings = currentTotal - vipTotal;

                    if (potentialSavings > 0) {
                      return (
                        <P.Small tw="font-bold mt-6">
                          You can save ${potentialSavings.toFixed(2)} on this
                          order by becoming a VIP Member!{" "}
                          <a
                            href="https://food.dwellsocial.com/dwellsocial-vip-membership/"
                            target="_blank"
                            rel="noopener noreferrer"
                            tw="text-primary hover:text-primary-dark underline"
                          >
                            Learn more here.
                          </a>
                        </P.Small>
                      );
                    }
                  }

                  // Fallback to original message if we can't calculate savings
                  return (
                    <P.Small tw="font-bold mt-6">
                      Save ${DWELLSOCIAL_RESERVATION_FEE} or more on every order
                      with a DwellSocial VIP Membership.{" "}
                      <a
                        href="https://food.dwellsocial.com/dwellsocial-vip-membership/"
                        target="_blank"
                        rel="noopener noreferrer"
                        tw="text-primary hover:text-primary-dark underline"
                      >
                        Learn more here.
                      </a>
                    </P.Small>
                  );
                })()}
              </p>
            </li>
          </ul>
        </div>
      )}
    </aside>
  );
};

export default OrderSummary;
